import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { Form, message, Select, Input, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../utils/formatters";
import { apiClient } from "../Api/AllApi";
import "../Styles/CartShop.css";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const EditOrder = () => {
    console.log('EditOrder page loaded');
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const mainContentRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [orderDetails, setOrderDetails] = useState(null);
    const [cart, setCart] = useState([]);
    const [customer, setCustomer] = useState(null);
    const [accountant, setAccountant] = useState(null);
    const [amountPaid, setAmountPaid] = useState(0);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [accountants, setAccountants] = useState([]);
    const [payments, setPayments] = useState([]);
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [allowAdjustments, setAllowAdjustments] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [paymentSender, setPaymentSender] = useState('');
    const [paymentReceiver, setPaymentReceiver] = useState('');
    const [storeProducts, setStoreProducts] = useState([]);
    const [newProductId, setNewProductId] = useState(null);
    const [newProductQty, setNewProductQty] = useState(1);
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const isMobile = window.innerWidth <= 768;

    useEffect(() => {
        setLoading(true);
        apiClient.get(`/orders/${id}`)
            .then((response) => {
                // Normalize numeric fields (API may return formatted strings like "50,000")
                const res = { ...response.data };
                const toNumber = (v) => {
                    if (v === null || v === undefined) return 0;
                    if (typeof v === 'number') return v;
                    try {
                        return parseFloat(String(v).replace(/,/g, '')) || 0;
                    } catch (e) {
                        return 0;
                    }
                };

                res.amount_paid = toNumber(res.amount_paid);
                res.total_price = toNumber(res.total_price);

                // Normalize order_products
                const normalizedProducts = (res.order_products || []).map(item => {
                    const normalized = { ...item };
                    normalized.quantity = parseInt(normalized.quantity, 10) || 0;
                    normalized.single_price = toNumber(normalized.single_price);
                    normalized.sub_qty = toNumber(normalized.sub_qty) || null;
                    normalized.sub_price = toNumber(normalized.sub_price) || null;
                    return normalized;
                });

                res.order_products = normalizedProducts;
                setOrderDetails(res);
                // Map order_products to cart state for editing (use normalized values)
                setCart(normalizedProducts.map(item => ({
                    product_id: item.product_id,
                    quantity: item.quantity,
                    single_price: item.single_price,
                    sub_qty: item.sub_qty,
                    sub_price: item.sub_price,
                    product: item.product, // keep full product info for UI
                })));
                setCustomer(res.customer?.id || null);
                setAccountant(res.accountant || null);
                setAmountPaid(res.amount_paid || 0);
                setPaymentMethod(response.data.payment_method || 'cash');
                // If orderDetails contains previous sender/receiver store them later in orderDetails state
                // Fetch payments related to this order if available via API
                setPaymentsLoading(true);
                apiClient.get(`/orders/${id}/payments`).then(pr => {
                    setPayments(pr.data || []);
                }).catch(() => {
                    // fallback: some APIs return payments via customer relation; leave empty
                    setPayments([]);
                }).finally(() => setPaymentsLoading(false));
            })
            .catch((error) => {
                message.error(t("Failed to load order for editing"));
            })
            .finally(() => setLoading(false));
        // Fetch customers/accountants/products if needed
        apiClient.get("/customers").then(res => setCustomerOptions(res.data));
        apiClient.get("/accountants").then(res => setAccountants(res.data.data || []));
        apiClient.get("/products").then(res => setStoreProducts(res.data || [])).catch(() => setStoreProducts([]));
    }, [id, t]);

    const getTranslatedName = useCallback(
        (nameInput) => {
            let nameTranslations;
            try {
                nameTranslations = typeof nameInput === "string" ? JSON.parse(nameInput) : nameInput;
            } catch (error) {
                return "Unnamed Product";
            }
            return nameTranslations?.[i18n.language] || nameTranslations?.en || "N/A";
        },
        [i18n.language]
    );

    const getItemSubtotal = (item) => {
        // Always calculate subtotal as quantity × single_price (not sub_qty × sub_price)
        const subtotal = item.editedSubtotal !== undefined
            ? item.editedSubtotal
            : item.quantity * item.single_price;
        // Round to 2 decimal places to avoid floating-point precision errors
        return Math.round(subtotal * 100) / 100;
    };

    const handlePriceChange = (index, newPrice) => {
        setCart(prev => prev.map((item, i) => {
            if (i !== index) return item;
            const price = parseFloat(newPrice) || 0;
            const qty = item.quantity;
            const subQty = item.sub_qty || 0;

            // Calculate subtotal from price and quantity
            const subtotal = price * qty;

            // Calculate sub price from price and sub_unit_ratio
            const subUnitRatio = item.product?.sub_unit_ratio || 1;
            const subPrice = subUnitRatio > 0 ? price / subUnitRatio : 0;

            return {
                ...item,
                single_price: price,
                editedSubtotal: subtotal,
                sub_price: subQty > 0 ? subPrice : item.sub_price,
            };
        }));
    };

    const handleCartChange = (index, field, value) => {
        setCart(prev => prev.map((item, i) => {
            if (i !== index) return item;
            let updated = { ...item, [field]: value };

            // When quantity or sub_qty changes, update subtotal and sub_price
            if (field === "quantity") {
                const newQty = value;
                const price = item.single_price;
                const subtotal = price * newQty;

                updated.editedSubtotal = subtotal;

                // Update sub_qty proportionally if it was set
                if (item.sub_qty && item.product?.sub_unit_ratio) {
                    updated.sub_qty = newQty * item.product.sub_unit_ratio;
                }

                // Recalculate sub_price from subtotal and new sub_qty
                if (updated.sub_qty > 0) {
                    updated.sub_price = subtotal / updated.sub_qty;
                }
            } else if (field === "sub_qty") {
                // When sub_qty changes, recalculate sub_price from current subtotal
                const subtotal = item.editedSubtotal !== undefined
                    ? item.editedSubtotal
                    : item.quantity * item.single_price;

                if (value > 0) {
                    updated.sub_price = subtotal / value;
                }
            }

            return updated;
        }));
    };

    const removeCartItem = (index) => {
        setCart(prev => prev.filter((_, i) => i !== index));
    };

    const addCartItem = () => {
        if (!newProductId) return message.error(t('Please select a product to add'));
        const product = storeProducts.find(p => p.id === newProductId);
        if (!product) return message.error(t('Selected product not found'));
        const item = {
            product_id: product.id,
            quantity: newProductQty || 1,
            single_price: parseFloat(product.price || 0),
            sub_qty: product.sub_unit_ratio ? (newProductQty * (product.sub_unit_ratio || 1)) : null,
            sub_price: product.sub_unit_price || null,
            product: product,
        };
        setCart(prev => [...prev, item]);
        setNewProductId(null);
        setNewProductQty(1);
    };

    const calculateSubAmount = useCallback(() => {
        let total = 0;
        cart.forEach((item) => {
            total += getItemSubtotal(item);
        });
        // Round to 2 decimal places to avoid floating-point precision errors
        return Math.round(total * 100) / 100;
    }, [cart]);

    const handleSubmit = async () => {
        // Prepare payload for backend update
        const orderJson = {
            customer_id: customer,
            accountant,
            amount_paid: amountPaid,
            payment_method: paymentMethod,
            sender: paymentSender || null,
            receiver: paymentReceiver || null,
            order_items: cart.map(item => ({
                product_id: item.product_id,
                quantity: item.quantity,
                single_price: item.single_price,
                sub_qty: item.sub_qty,
                sub_price: item.sub_price,
            })),
        };
        try {
            // If payments exist and adjustments are not allowed, block the update
            if (payments.length > 0 && !allowAdjustments) {
                message.error(t("This order has payments. Enable adjustments to proceed or contact accounting."));
                return;
            }
            // Attach create_adjustment flag when adjustments are allowed
            if (payments.length > 0 && allowAdjustments) {
                orderJson.create_adjustment = true;
            }
            await apiClient.put(`/orders/${id}`, orderJson);
            message.success(t("Order updated successfully"));
            navigate("/TableOrder");
        } catch (error) {
            message.error(t("Failed to update order"));
        }
    };

    if (loading) {
        return <Spin tip={t("Loading order...")} />;
    }

    return (
        <div style={{ direction: "rtl", padding: "20px" }}>
            <Typography variant="h5" style={{ marginBottom: 20 }}>{t("Edit Order")}</Typography>
            <div style={{ overflowX: "auto", width: "100%" }}>
                <Table style={{ minWidth: isMobile ? "100%" : "600px" }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t("Product")}</TableCell>
                            <TableCell>{t("Quantity")}</TableCell>
                            <TableCell>{t("Single Price")}</TableCell>
                            <TableCell>{t("Sub Qty")}</TableCell>
                            <TableCell>{t("Sub Price")}</TableCell>
                            <TableCell>{t("Subtotal")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {cart.map((item, idx) => (
                            <TableRow key={item.product_id}>
                                <TableCell>{getTranslatedName(item.product?.name) || item.product_id}</TableCell>
                                <TableCell>
                                    <Input
                                        type="number"
                                        value={item.quantity}
                                        onChange={e => handleCartChange(idx, "quantity", parseInt(e.target.value, 10) || 0)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Input
                                        type="number"
                                        value={Math.round(item.single_price * 100) / 100}
                                        onChange={e => handlePriceChange(idx, e.target.value)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Input
                                        type="number"
                                        value={item.sub_qty || ""}
                                        onChange={e => handleCartChange(idx, "sub_qty", parseFloat(e.target.value) || 0)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Input
                                        type="number"
                                        value={item.sub_price ? Math.round(item.sub_price * 100) / 100 : ""}
                                        disabled
                                    />
                                </TableCell>
                                <TableCell>
                                    {getItemSubtotal(item).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell>
                                    <Button onClick={() => removeCartItem(idx)} style={{ background: '#FA4032', color: 'white' }}>{t('Remove')}</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {/* Add product control */}
                <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
                    <Select value={newProductId} onChange={setNewProductId} style={{ minWidth: 240 }} placeholder={t('Select product to add')}>
                        {storeProducts.map(p => (
                            <Select.Option key={p.id} value={p.id}>{getTranslatedName(p.name)} - {formatCurrency(p.price || 0, t('IQD'))}</Select.Option>
                        ))}
                    </Select>
                    <Input type="number" min={1} value={newProductQty} onChange={e => setNewProductQty(parseInt(e.target.value, 10) || 1)} style={{ width: 100 }} />
                    <Button type="primary" onClick={addCartItem}>{t('Add Product')}</Button>
                </div>
            </div>
            <div className="total" style={{ marginTop: "20px" }}>
                <Typography variant="h6">{t("Cart Subtotal")}:</Typography>
                <Typography variant="h6" color="green">
                    {formatCurrency(calculateSubAmount(), t("IQD"))}
                </Typography>
            </div>
            <div style={{ marginTop: 16 }}>
                <Typography variant="subtitle1">{t("Payments")}</Typography>
                {paymentsLoading ? (
                    <Spin />
                ) : payments.length === 0 ? (
                    <div>{t("No payments recorded for this order.")}</div>
                ) : (
                    <div>
                        <div style={{ marginBottom: 8 }}>
                            <b>{t("Total Paid")}:</b> {formatCurrency(payments.reduce((s, p) => s + Number(p.amount || 0), 0), t("IQD"))}
                        </div>
                        <div style={{ maxHeight: 160, overflow: 'auto', border: '1px solid #eee', padding: 8 }}>
                            {payments.map((p, i) => (
                                <div key={i} style={{ marginBottom: 6 }}>
                                    <div><b>{dayjs(p.created_at).format('DD/MM/YYYY HH:mm')}</b></div>
                                    <div>{t('Amount')}: {formatCurrency(p.amount, t('IQD'))} — {p.note || ''}</div>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: 8 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type="checkbox" checked={allowAdjustments} onChange={e => setAllowAdjustments(e.target.checked)} />
                                <span>{t('Allow adjustments to this order (will create adjustment records)')}</span>
                            </label>
                        </div>
                    </div>
                )}
            </div>
            <Form
                form={form}
                layout="vertical"
                style={{ maxWidth: "400px", margin: "30px auto 0 auto" }}
            >
                <Form.Item
                    label={t("Select Customer")}
                    name="customer"
                    initialValue={customer}
                    rules={[{ required: true, message: t("Please select customer") }]}
                >
                    <Select
                        placeholder={t("Select a customer")}
                        onChange={setCustomer}
                        value={customer}
                    >
                        {customerOptions.map((cust) => (
                            <Select.Option key={cust.id} value={cust.id}>
                                {cust.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    label={t("Select Accountant")}
                    name="accountant"
                    initialValue={accountant}
                    rules={[{ required: true, message: t("Please select accountant") }]}
                >
                    <Select
                        placeholder={t("Select an accountant")}
                        onChange={setAccountant}
                        value={accountant}
                    >
                        {accountants.map((acc) => (
                            <Select.Option key={acc.id} value={acc.id}>
                                {acc.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    label={t("Amount Paid")}
                    name="amount_paid"
                    initialValue={amountPaid}
                    rules={[{ required: true, message: t("Please enter paid amount") }]}
                >
                    <Input
                        type="number"
                        value={amountPaid}
                        onChange={e => setAmountPaid(Number(e.target.value))}
                    />
                </Form.Item>
            </Form>
            <Button type="primary" onClick={handleSubmit} style={{ marginTop: 20 }}>{t("Save Changes")}</Button>
        </div>
    );
};

export default EditOrder;
